/**
 * App-level permissions for system administration.
 * These are NOT scoped to a household.
 */
export const APP_PERMISSIONS = {
  // User management
  'app:users:view': 'View all users and their roles',
  'app:users:edit': 'Edit user details and app roles',
  'app:users:restrict': 'Apply restrictions (ban custom names, suspend)',

  // System announcements
  'app:announcements:view': 'View all system announcements (including drafts)',
  'app:announcements:create': 'Create system announcements',
  'app:announcements:edit': 'Edit system announcements',
  'app:announcements:delete': 'Delete system announcements',

  // Audit & monitoring
  'app:audit:view': 'View admin audit logs',
  'app:metrics:view': 'View system metrics and analytics',

  // Translation
  'app:translations:manage': 'Manage translation overrides',

  // Products (global catalog)
  'app:products:view': 'View global product catalog',
  'app:products:create': 'Create products in global catalog',
  'app:products:edit': 'Edit global product data',
  'app:products:delete': 'Delete products from global catalog',
  'app:products:merge': 'Merge duplicate products',

  // Content moderation
  'app:moderation:view': 'View user-contributed content for review',
  'app:moderation:approve': 'Approve user contributions to global catalog',
  'app:moderation:reject': 'Reject user contributions',
  'app:moderation:edit': 'Edit user contributions before approval',

  // Support & debugging
  'app:support:view': 'View user data for support (households, items)',

  // Bulk operations
  'app:bulk:notify': 'Send bulk notifications to users',
  'app:bulk:export': 'Export system-wide data',

  // Subscriptions (billing)
  'app:subscriptions:view': 'View subscription data and billing status',
  'app:subscriptions:manage': 'Manage subscriptions, refunds, extensions',

  // System health
  'app:health:view': 'View system health and job status',
  'app:health:manage': 'Retry failed jobs, clear caches',
} as const;

export type AppPermission = keyof typeof APP_PERMISSIONS;

/**
 * App role types stored in users.app_role column.
 */
export type AppRole = 'superadmin' | 'admin' | 'user';

/**
 * Array of valid app roles for validation.
 */
export const APP_ROLES: readonly AppRole[] = ['superadmin', 'admin', 'user'] as const;

/**
 * Default permissions for each app role.
 */
export const APP_ROLE_PERMISSIONS: Record<AppRole, AppPermission[]> = {
  superadmin: Object.keys(APP_PERMISSIONS) as AppPermission[],

  admin: [
    // Announcements
    'app:announcements:view',
    'app:announcements:create',
    'app:announcements:edit',
    'app:announcements:delete',
    // Audit
    'app:audit:view',
    // Products (global catalog)
    'app:products:view',
    'app:products:create',
    'app:products:edit',
    'app:products:merge',
    // Content moderation
    'app:moderation:view',
    'app:moderation:approve',
    'app:moderation:reject',
    'app:moderation:edit',
    // Support
    'app:support:view',
    // Metrics (read-only)
    'app:metrics:view',
    // Subscriptions (view only)
    'app:subscriptions:view',
    // Health (view only)
    'app:health:view',
  ],

  user: [],
};

/**
 * App role display info.
 */
export const APP_ROLE_INFO: Record<AppRole, { name: string; description: string }> = {
  superadmin: {
    name: 'Super Admin',
    description: 'Full system access. Can manage other admins and all settings.',
  },
  admin: {
    name: 'Admin',
    description: 'Can manage announcements, products, users, and moderation.',
  },
  user: {
    name: 'User',
    description: 'Standard user. No administrative access.',
  },
};

/**
 * Check if an app role has a specific permission.
 */
export function hasAppPermission(role: AppRole, permission: AppPermission): boolean {
  return APP_ROLE_PERMISSIONS[role].includes(permission);
}
